import {
    ApolloCache,
    ApolloClient,
    useApolloClient,
} from '@thoughtspot/apollo-utils';
import { ErrorContext, ErrorContextType } from '@thoughtspot/blink-context';
import { BLINK_FLAGS, envFlags } from '@thoughtspot/env-flags';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import { isDataLimitExceedError } from '@thoughtspot/ts-chart-app';
import _ from 'lodash';
import { useContext } from 'react';
import { useBachLazyQuery, useBachQuery } from '/@services/bach/bach';
import { bachSessionWithAcSessionKeyQuery } from '/@services/bach/bach.util';
import {
    useGatedBachLazyQuery,
    useHydrationGate,
} from '/@services/bach/bach-gated';
import {
    DataQueryInput,
    GetAnswerDataByQueryDocument,
    GetAnswerDataByQueryQuery,
    GetAnswerDataByQueryQueryVariables,
    GetVizDataDocument,
    GetVizDataQuery,
    GetVizDataQueryVariables,
} from '/@services/generated/graphql-types';
import { getSession } from '/@services/stateful/stateful-service-hooks.util';
import { getErrorObjectFromGraphQLErrors } from '/@utils/error-handling/error-utils';

export const shouldCombineMultipleQueriesForCustomChart = () => {
    return isFeatureEnabled(
        FEATURE_FLAG_NAMES.COMBINE_MULTIPLE_QUERIES_FOR_CUSTOM_CHART,
        {
            urlParamValue: envFlags.getValue(
                BLINK_FLAGS.combineMultipleQueriesForCustomChart?.name,
            ),
        },
    );
};

export function useGetVizDataQueryByVizIds(
    answerSessionId: string,
    vizIds: string[],
    skip: boolean,
) {
    const { data, loading } = useBachQuery<
        GetVizDataQuery,
        GetVizDataQueryVariables
    >(answerSessionId, GetVizDataDocument, {
        variables: {
            vizIds,
        } as GetVizDataQueryVariables,
        skip,
    });
    return {
        vizData: data?.getVizData?.vizData,
        loading,
    };
}

export function useLazyGetVizDataQueryByVizIds(answerSessionId: string) {
    const [_getVizData, { loading }] = useGatedBachLazyQuery<
        GetVizDataQuery,
        GetVizDataQueryVariables
    >(answerSessionId, GetVizDataDocument, {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
    });
    return {
        getVizData: (vizIds: string[]) =>
            _getVizData({
                variables: {
                    vizIds,
                } as GetVizDataQueryVariables,
            }),
        loading,
    };
}

const getMaxRowCount = (data: GetAnswerDataByQueryQuery) => {
    const mappedData = data.getAnswerDataByQuery.data.map(
        dataArr => dataArr[0],
    );
    const maxRowCount = Math.max(...mappedData.map(item => item.totalRowCount));
    return maxRowCount;
};

export const getParsedQueryResponseForAnswerDataByQueryCall = (
    data: GetAnswerDataByQueryQuery,
) => {
    /**
        The response object is of following format
        responseObject = {
            getAnswerDataByQuery: {
                data: [
                    First Query Response -> [
                        {
                            "columnDataLite": [
                                {},
                                {},
                                ...
                            ],
                            "completionRatio": 1,
                            "samplingRatio": 1,
                            "totalRowCount": "8"
                        }
                    ],
                    Second Query Response -> [
                        {
                            "columnDataLite": [
                                {},
                                {},
                                ...
                            ],
                            "completionRatio": 1,
                            "samplingRatio": 1,
                            "totalRowCount": "8"
                        }
                    ]
                ]
            }
        }
    */
    const mappedData = data.getAnswerDataByQuery.data.map(
        dataArr => dataArr[0],
    );

    const parsedData = _.map(mappedData, dataRow => {
        return {
            ...dataRow,
            columnDataLite: _.map(dataRow.columnDataLite, colRow => ({
                ...colRow,
                dataValue: JSON.parse(colRow.dataValue),
            })),
        };
    });
    return parsedData;
};

export function useLazyGetAnswerDataByQuery(answerSessionId: string) {
    const client = useApolloClient();

    const errorCtx = useContext<ErrorContextType<string>>(ErrorContext);

    const { gateCall } = useHydrationGate();

    const { refetch } = useBachQuery<
        GetAnswerDataByQueryQuery,
        GetAnswerDataByQueryQueryVariables
    >(
        answerSessionId,
        GetAnswerDataByQueryDocument,
        {
            /**
             * We want all the data calls to be network only as we are not preserving data states.
             * Data states are not preserved as client(chart app) can call queries at random also
             * which will not be part of the actual queries. Hence chart is expected to maintain
             * data states.
             * Open Item: How will the filters and other question updates trigger chart renders?
             */
            fetchPolicy: 'cache-first',
            skip: true,
        },
        null,
        true,
        true,
    );

    return {
        getAnswerDataByQuery: (
            dataQueries: DataQueryInput,
            vizId: string,
            fetchFreshData = false,
            trackDataQueryEventForCustomChart: (
                dataQueryInput: DataQueryInput,
                totalRowCount: number,
                queryTimingMs?: number,
                isCacheHit?: boolean,
            ) => void,
        ): Promise<JSON[]> => {
            // Start timing for this query
            const startTime = performance.now();

            const combineQueries = shouldCombineMultipleQueriesForCustomChart();
            const session = getSession(
                client,
                answerSessionId,
                'BachSessionId',
                bachSessionWithAcSessionKeyQuery,
                ['acSession'],
            );

            // Try to read from cache using NORMALIZED query
            const cacheVariables = {
                session: {
                    acSession: session.acSession,
                },
                dataQueries,
                vizId,
                combineQueries,
            } as GetAnswerDataByQueryQueryVariables;

            const cachedQueryResponse = client.cache.readQuery({
                query: GetAnswerDataByQueryDocument,
                variables: cacheVariables,
            }) as GetAnswerDataByQueryQuery;

            if (!_.isNil(cachedQueryResponse) && !fetchFreshData) {
                const endTime = performance.now();
                const queryTimingMs = endTime - startTime;
                const maxRowCount = getMaxRowCount(cachedQueryResponse);
                trackDataQueryEventForCustomChart(
                    dataQueries,
                    maxRowCount,
                    queryTimingMs,
                    true, // cache hit
                );
                return Promise.resolve(
                    getParsedQueryResponseForAnswerDataByQueryCall(
                        cachedQueryResponse,
                    ),
                );
            }

            // Use NORMALIZED query for the network request too, so it gets cached with normalized key.
            // Wrap with gateCall so the refetch is deferred until isHydrated = true,
            // preventing NW_ERROR when called before the answer session is ready.
            return gateCall(() =>
                refetch({
                    session,
                    dataQueries,
                    vizId,
                    combineQueries,
                }),
            )
                .then(responseObject => {
                    const endTime = performance.now();
                    const queryTimingMs = endTime - startTime;
                    const maxRowCount = getMaxRowCount(responseObject.data);
                    trackDataQueryEventForCustomChart(
                        dataQueries,
                        maxRowCount,
                        queryTimingMs,
                        false, // network call
                    );
                    return getParsedQueryResponseForAnswerDataByQueryCall(
                        responseObject.data,
                    );
                })
                .catch(err => {
                    const errors = getErrorObjectFromGraphQLErrors<string>(
                        err,
                        GetAnswerDataByQueryDocument,
                    );
                    const rootError = errors[0];
                    // Data limit errors need special handling to allow custom chart initialization
                    // Do NOT post them to the error context - they will be handled by ts-chart-app's error boundary
                    // This allows users to modify chart config/visual props to fix the issue
                    // (e.g., add aggregations, change column selections, reduce number of measures, add facets, etc.)
                    // If posted to error context, it would show as a generic error and prevent chart initialization
                    if (isDataLimitExceedError(rootError)) {
                        throw rootError;
                    }
                    errorCtx.postError(errors);
                    throw errors;
                });
        },
    };
}
